import { useCallback, useMemo } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { Layout } from '@components';
import { useApplicationStore } from '@data';
import { PageHeading } from '@quickmd-llc/design-system';
import { Events, tracking } from '@lib';
import { IntakeForm, QuestionType } from '@qmd-types/intakeForms';
import { VisitDetail } from '@qmd-types/visits';
import { BOOKING_CONFIRMATION_ROUTE_PATHNAME } from '@router/routes/routePathnames';
import {
  IntakeFormQuestion,
  IntakeFormReview,
  PrepopulatedBanner,
  VisitContext,
} from './components';
import { IntakeFormProvider } from './context/IntakeFormProvider';
import { useIntakeForm } from './context/useIntakeForm';

type Props = {
  visit: VisitDetail;
};

function IntakeFormPage({ visit }: Props) {
  const {
    editQuestion,
    handleEditQuestionResponse,
    intakeForm,
    showIntakeFormReview,
    setStep,
    step,
  } = useIntakeForm();
  const navigate = useNavigate();
  const navigationSteps = intakeForm?.questions.length;

  const pageTitle = useMemo(() => {
    if (editQuestion) {
      return 'Edit response';
    }

    return showIntakeFormReview ? 'Review intake form' : 'Intake form';
  }, [editQuestion, showIntakeFormReview]);
  const hideProgressBar = Boolean(editQuestion || showIntakeFormReview);
  const showPrepopulatedBanner = intakeForm.isPrepopulated && step === 1;

  const handleOnClickBack = useCallback(() => {
    if (editQuestion) {
      tracking.trackEvent(Events.CLOSE_INTAKE_RESPONSE);

      handleEditQuestionResponse(null);
      return;
    }

    if (step === 1) {
      navigate(-1);
      return;
    }

    setStep(step - 1);
  }, [editQuestion, handleEditQuestionResponse, navigate, setStep, step]);

  return (
    <Layout
      currentPosition={step}
      className="flex flex-col gap-4"
      navigationSteps={navigationSteps}
      onBackClick={handleOnClickBack}
      hideBackButton={showIntakeFormReview}
      hideProgressBar={hideProgressBar}
    >
      <div className="grid gap-4">
        <div className="grid gap-1">
          <PageHeading>{pageTitle}</PageHeading>
          <VisitContext visit={visit} />
        </div>

        {showPrepopulatedBanner && <PrepopulatedBanner />}
      </div>

      {showIntakeFormReview ? (
        <IntakeFormReview />
      ) : (
        <IntakeFormQuestion question={editQuestion} />
      )}
    </Layout>
  );
}

export function IntakeFormWithProvider(): JSX.Element | null {
  const navigate = useNavigate();
  const visit = useApplicationStore(({ patient }) => patient.visitDetail);
  const intakeForm = useLoaderData() as IntakeForm;

  if (!visit || !intakeForm) {
    navigate(BOOKING_CONFIRMATION_ROUTE_PATHNAME);
    return null;
  }

  // @NOTE: Intake form file upload type questions are not supported
  const intakeFormFilteredForFileQuestions = {
    ...intakeForm,
    questions: intakeForm.questions.filter(
      ({ type }) => type !== QuestionType.FILE,
    ),
  };

  return (
    <IntakeFormProvider intakeForm={intakeFormFilteredForFileQuestions}>
      <IntakeFormPage visit={visit} />
    </IntakeFormProvider>
  );
}
